/**
 * Detects WS `PARCEL_STATUS_UPDATE` events that carry the top-level
 * `routeJumped: true` flag and runs the user-facing
 * route-recalculation window for the caller:
 *
 *   - Sets a local reactive `isActive` flag to `true` (returned to the
 *     caller; consumed e.g. by `EtaCard` for its progress-circle
 *     variant override or by `useParcelMarkerAnimation` to gate its
 *     leg-change processing).
 *   - When `silent` is false (the default), also opens a warning toast
 *     via the alert store.
 *   - Starts a fixed-length local timer; on expiry and flips `isActive`
 *     back to `false`.
 */
import { inject, onBeforeUnmount, ref, watch } from 'vue';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useAlertStore } from '@/stores/alert.js';
import { sendAnalytics } from '@/services/analytics.js';

export const ROUTE_JUMP_HOLD_MS = 5000;

/**
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] When `true`, this instance
 *   does NOT open or clear the toast — it only manages the local
 *   `isActive` flag. Use from non-orchestrator callers (e.g. EtaCard)
 *   that just need the timed boolean to drive a variant change.
 * @returns {{ isActive: import('vue').Ref<boolean> }}
 */
export function useRouteJumpHandler({ silent = false } = {}) {
  const trackingDataStore = useTrackingDataStore();
  const alertStore = silent ? null : useAlertStore();
  const $t = inject('t');

  const isActive = ref(false);
  let timerId = null;

  const clearTimer = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const onRelease = () => {
    timerId = null;
    isActive.value = false;
    if (alertStore) {
      alertStore.setAlert(
        {
          title: $t('routeJump.recalculated.title'),
          description: $t('routeJump.recalculated.description')
        },
        'success'
      );
    }
  };

  const holdRoute = () => {
    clearTimer();
    if (alertStore) {
      alertStore.setAlert(
        {
          title: $t('routeJump.recalculating.title'),
          description: $t('routeJump.recalculating.description')
        },
        'warning'
      );
      sendAnalytics(trackingDataStore.data, 'map', 'route_recalculated', {});
    }
    isActive.value = true;
    timerId = setTimeout(onRelease, ROUTE_JUMP_HOLD_MS);
  };

  const stopWatch = watch(
    () => trackingDataStore.routeJumpCount,
    (count, prev) => {
      if (count > (prev ?? 0)) holdRoute();
    }
  );

  onBeforeUnmount(() => {
    stopWatch();
    clearTimer();
  });

  return { isActive };
}
